
import React, { useState } from 'react';
import { Upload, X, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { validateFileUpload } from '@/lib/security/validation';
import { toast } from 'sonner';

interface FileUploadProps {
  file: File | null;
  setFile: (file: File | null) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ file, setFile }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileValidation = (selectedFile: File): boolean => {
    const validation = validateFileUpload(selectedFile);
    
    if (!validation.isValid) {
      toast.error(validation.error || 'Invalid file');
      return false;
    }
    
    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    try {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && handleFileValidation(droppedFile)) {
        console.log('PDF file dropped:', droppedFile.name);
        setFile(droppedFile);
      }
    } catch (error) {
      console.error('Error handling dropped file:', error);
      toast.error('Error processing dropped file');
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFile = e.target.files?.[0];
      if (selectedFile && handleFileValidation(selectedFile)) {
        console.log('PDF file selected:', selectedFile.name);
        setFile(selectedFile);
      }
      
      // Clear the input to allow re-selecting the same file
      e.target.value = '';
    } catch (error) {
      console.error('Error handling selected file:', error);
      toast.error('Error processing selected file');
    }
  };
  
  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
    console.log('File removed');
  };

  return (
    <div 
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : 'border-border'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => document.getElementById('file-upload')?.click()}
    >
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <File className="h-8 w-8 text-primary" />
          <div className="text-left">
            <p className="font-medium text-sm mb-0.5">{file.name}</p>
            <p className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <Button 
            type="button" 
            variant="outline"
            size="sm"
            className="ml-4"
            onClick={handleRemoveFile}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="py-4">
          <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
          <p className="font-medium text-sm mb-1">
            Drag and drop your PDF file here
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            or click to browse your files (PDF only, max 50MB)
          </p>
          <Button type="button" variant="outline" size="sm">
            Browse Files
          </Button>
        </div>
      )}
      <input
        id="file-upload"
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};
